import Dexie, { type EntityTable } from 'dexie';
import type {
  Ingredient,
  LoyaltyCard,
  Product,
  PriceObservation,
  PurchaseHint,
  Recipe,
  ShoppingHistoryEntry,
  ShoppingList,
  ShoppingListItem,
  Store,
} from '@recipes/contracts';

/** A recipe the user explicitly made available offline, plus its resized images. */
export interface OfflineRecipe {
  id: string;
  recipe: Recipe;
  savedAt: number;
  /** Collection ids that caused this download (empty when downloaded directly). */
  viaCollections: string[];
  /** Recipes that link to this one as a base recipe and brought it along. */
  viaRecipes?: string[];
  /** false = only here because something else needed it; goes when that goes. */
  explicit?: boolean;
}

export interface OfflineImage {
  /** Asset id + variant, e.g. "abc:medium" */
  key: string;
  assetId: string;
  blob: Blob;
  mimeType: string;
  savedAt: number;
}

/** Mutations queued while offline; replayed by the sync manager when back online. */
export interface PendingAction {
  id?: number;
  kind: 'cooking-note' | 'step-check' | 'cooking-log' | 'favorite' | 'rating' | 'shopping-check';
  recipeId: string | null;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export interface CookingSession {
  recipeId: string;
  currentStep: number;
  servings: string;
  checkedIngredients: string[];
  completedSteps: string[];
  notes: string[];
  updatedAt: number;
}

/** A queued action the server rejected with a version conflict; the user resolves it. */
export interface SyncConflict {
  id?: number;
  kind: PendingAction['kind'];
  recipeId: string;
  /** What the user wrote locally (e.g. the cooking note). */
  localPayload: unknown;
  /** The server's current version of the recipe at the time of the conflict. */
  serverRecipe: Recipe | null;
  message: string;
  createdAt: number;
}

/** One photo of a queued dish; uploaded when the visit is synchronized. */
export interface PendingTastingPhoto {
  blob: Blob;
  name: string;
  mimeType: string;
  altText: string | null;
  /** Set once uploaded, so a retried sync does not upload the photo twice. */
  assetId: string | null;
}

export interface PendingTastingNote {
  dishName: string | null;
  notes: string;
  flavorTags: string[];
  rating: number | null;
  photos: PendingTastingPhoto[];
}

/**
 * A tasting-note visit captured while offline (or whose upload failed). `id` doubles as the
 * idempotency key of POST /visits, so the visit is created exactly once however often the
 * sync is retried.
 */
export interface PendingVisit {
  id: string;
  /** Existing visit to append to; null creates a new visit. */
  visitId: string | null;
  placeId: string | null;
  visitedAt: string;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  locationSource: 'DEVICE' | 'EXIF' | 'MANUAL' | 'NONE';
  notes: PendingTastingNote[];
  createdAt: number;
  attempts: number;
  lastError?: string;
}

/** Cached OpenStreetMap lookups (nearby venues, search, reverse geocoding), 24 h TTL. */
export interface CachedPlacesLookup {
  key: string;
  value: unknown;
  savedAt: number;
}

// ---------------------------------------------------------------------------
// Ingredient catalog: device copy (docs/finished/ingredient-catalog-spec.md 6.9)
// ---------------------------------------------------------------------------

export interface CatalogIngredientRow {
  id: string;
  normalizedName: string;
  /** Normalized alias names, multi-entry indexed for offline name resolution. */
  aliasKeys: string[];
  ingredient: Ingredient;
}

export interface CatalogProductRow {
  id: string;
  barcode: string | null;
  ingredientId: string | null;
  /** sync = part of the household copy; lookup = looked up on this device; local = captured here, not confirmed yet. */
  origin: 'sync' | 'lookup' | 'local';
  product: Product;
  savedAt: number;
}

export interface CatalogStoreRow {
  id: string;
  store: Store;
  /** Written on this device and not confirmed by the server yet. */
  pending?: boolean;
}

export interface CatalogHintRow {
  id: string;
  ingredientId: string | null;
  productId: string | null;
  storeId: string | null;
  hint: PurchaseHint;
  pending?: boolean;
}

/** A price the household has seen (docs/partial/shopping-in-store-spec.md 6.3). */
export interface CatalogPriceRow {
  id: string;
  productId: string | null;
  ingredientId: string | null;
  storeId: string | null;
  chainId: string | null;
  price: PriceObservation;
  /** Typed on this device and not confirmed by the server yet. */
  pending?: boolean;
}

/** A photo of a price tag or of a receipt taken without a connection (spec 6.4, 6.5). */
export interface PendingTripPhotoRow {
  id: string;
  kind: 'price-tag' | 'receipt';
  /** One photo for a tag, several in reading order for a receipt. */
  photos: Array<{ blob: Blob; name: string; mimeType: string; assetId: string | null }>;
  productId: string | null;
  storeId: string | null;
  shoppingListId: string | null;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export interface CatalogThumbRow {
  productId: string;
  blob: Blob;
  savedAt: number;
}

export interface CatalogLoyaltyCardRow {
  id: string;
  card: LoyaltyCard;
  /** Created on this device and not confirmed by the server yet. */
  pending?: boolean;
}

/** The picture of a loyalty card as a blob: the checkout scanner needs it without a connection. */
export interface CatalogCardImageRow {
  cardId: string;
  /** The asset this picture came from; null while it waits for its upload. */
  assetId: string | null;
  blob: Blob;
  savedAt: number;
}

export interface CatalogMetaRow {
  key: string;
  value: unknown;
}

export interface PendingCatalogPhoto {
  blob: Blob;
  name: string;
  mimeType: string;
  role: 'FRONT' | 'LABEL';
  /** What the upload is for; a product photo when absent. */
  purpose?: 'PRODUCT' | 'LOYALTY_CARD';
  /** Set once uploaded, so a retried sync does not upload the photo twice. */
  assetId: string | null;
}

/** A catalog change made on this device; replayed in order, never dropped silently. */
export interface PendingCatalogAction {
  id?: number;
  kind:
    | 'product-create'
    | 'product-update'
    | 'hint-create'
    | 'hint-update'
    | 'hint-delete'
    | 'store-create'
    | 'store-update'
    | 'price-create'
    | 'price-update'
    | 'price-delete'
    | 'loyalty-card-create'
    | 'loyalty-card-update'
    | 'loyalty-card-delete';
  /** Id of the product, hint or store the action is about (client-generated for creates). */
  entityId: string;
  payload: unknown;
  photos?: PendingCatalogPhoto[];
  createdAt: number;
  attempts: number;
  lastError?: string;
}

// ---------------------------------------------------------------------------
// Shopping lists: device copy (docs/partial/shopping-list-extensions-spec.md 6.7)
// ---------------------------------------------------------------------------

export interface ShoppingListRow {
  id: string;
  list: ShoppingList;
  /** Created on this device and not confirmed by the server yet. */
  pending?: boolean;
}

export interface ShoppingItemRow {
  id: string;
  listId: string;
  item: ShoppingListItem;
  /** Changed on this device; the change still waits in the queue. */
  pending?: boolean;
}

export interface ShoppingHistoryRow {
  id: string;
  normalizedName: string;
  entry: ShoppingHistoryEntry;
}

export interface ShoppingMetaRow {
  key: string;
  value: unknown;
}

/** A list change made on this device; replayed in order per list, never dropped silently. */
export interface PendingShoppingAction {
  id?: number;
  kind: 'list-create' | 'item-add' | 'item-check' | 'item-update' | 'item-remove' | 'clear-bought';
  listId: string;
  /** The item (or list) the action is about; for adds the local id of the affected item. */
  entityId: string;
  /** Idempotency key for creates and adds. */
  key: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export class RecipesOfflineDb extends Dexie {
  offlineRecipes!: EntityTable<OfflineRecipe, 'id'>;
  offlineImages!: EntityTable<OfflineImage, 'key'>;
  pendingActions!: EntityTable<PendingAction, 'id'>;
  cookingSessions!: EntityTable<CookingSession, 'recipeId'>;
  syncConflicts!: EntityTable<SyncConflict, 'id'>;
  pendingVisits!: EntityTable<PendingVisit, 'id'>;
  placesCache!: EntityTable<CachedPlacesLookup, 'key'>;
  catalogIngredients!: EntityTable<CatalogIngredientRow, 'id'>;
  catalogProducts!: EntityTable<CatalogProductRow, 'id'>;
  catalogStores!: EntityTable<CatalogStoreRow, 'id'>;
  catalogHints!: EntityTable<CatalogHintRow, 'id'>;
  catalogThumbs!: EntityTable<CatalogThumbRow, 'productId'>;
  catalogMeta!: EntityTable<CatalogMetaRow, 'key'>;
  pendingCatalog!: EntityTable<PendingCatalogAction, 'id'>;
  shoppingLists!: EntityTable<ShoppingListRow, 'id'>;
  shoppingItems!: EntityTable<ShoppingItemRow, 'id'>;
  shoppingHistory!: EntityTable<ShoppingHistoryRow, 'id'>;
  shoppingMeta!: EntityTable<ShoppingMetaRow, 'key'>;
  pendingShopping!: EntityTable<PendingShoppingAction, 'id'>;
  catalogPrices!: EntityTable<CatalogPriceRow, 'id'>;
  pendingTripPhotos!: EntityTable<PendingTripPhotoRow, 'id'>;
  catalogLoyaltyCards!: EntityTable<CatalogLoyaltyCardRow, 'id'>;
  catalogCardImages!: EntityTable<CatalogCardImageRow, 'cardId'>;

  constructor() {
    super('recipes-manager');
    this.version(1).stores({
      offlineRecipes: 'id, savedAt, *viaCollections',
      offlineImages: 'key, assetId, savedAt',
      pendingActions: '++id, kind, recipeId, createdAt',
      cookingSessions: 'recipeId, updatedAt',
    });
    // v2 (additive): conflicts produced while replaying the pending-action queue.
    this.version(2).stores({
      syncConflicts: '++id, recipeId, createdAt',
    });
    // v3 (additive): tasting notes – offline capture queue and the OSM lookup cache.
    this.version(3).stores({
      pendingVisits: 'id, createdAt',
      placesCache: 'key, savedAt',
    });
    // v4 (additive): ingredient catalog – device copy and the queue of offline changes.
    this.version(4).stores({
      catalogIngredients: 'id, normalizedName, *aliasKeys',
      catalogProducts: 'id, barcode, ingredientId, origin, savedAt',
      catalogStores: 'id',
      catalogHints: 'id, ingredientId, productId, storeId',
      catalogThumbs: 'productId, savedAt',
      catalogMeta: 'key',
      pendingCatalog: '++id, createdAt, entityId',
    });
    // v5 (additive): shopping lists – device copy, purchase history and the offline queue.
    this.version(5).stores({
      shoppingLists: 'id',
      shoppingItems: 'id, listId',
      shoppingHistory: 'id, normalizedName',
      shoppingMeta: 'key',
      pendingShopping: '++id, listId, entityId, createdAt',
    });
    // v6 (additive): prices of the household and photos waiting for a connection.
    this.version(6).stores({
      catalogPrices: 'id, productId, ingredientId, storeId, chainId',
      pendingTripPhotos: 'id, kind, createdAt',
    });
    // v7 (additive): loyalty cards of the household and their pictures for the checkout.
    this.version(7).stores({
      catalogLoyaltyCards: 'id',
      catalogCardImages: 'cardId, assetId',
    });
  }
}

export const offlineDb = new RecipesOfflineDb();
